import { createAsyncThunk } from "@reduxjs/toolkit";

import { trackCartItemRemoval } from "@app/modules/checkout/actions/ancillary";
import { pollCartV2Success } from "@app/modules/checkout/store/v2/slices/cart";
import type { NapiClient } from "@app/services/api";
import type { CheckoutReduxStateV2 } from "@app/types/checkout-v2-types";
import type { Ancillary } from "@app/types/napi-v2-ancillary";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import type { Dispatch } from "@reduxjs/toolkit";

export const addCartItemCandidate = createAsyncThunk(
  "checkout/addCartItemCandidateStatus",
  async (
    {
      api,
      cart_item_id
    }: {
      api: NapiClient;
      cart_item_id: string;
    },
    { getState, dispatch }
  ) => {
    const state = getState() as CheckoutReduxStateV2 | ResultsReduxState;
    const cart_id =
      (state as CheckoutReduxStateV2).cart?.id ||
      (state as ResultsReduxState).cart?.cart?.id;
    const cart_items = state.cart_items.value;

    if (!cart_id) {
      return;
    }

    try {
      const updated_cart = await api.selectAncillaryForCart(
        cart_id,
        cart_item_id
      );
      dispatch(pollCartV2Success(updated_cart));
      const cart_item = cart_items?.find(({ id }) => id === cart_item_id);

      return {
        ...cart_item,
        status: "SELECTED"
      };
    } catch (err) {
      throw err;
    }
  }
);

export const removeCartItem = createAsyncThunk(
  "checkout/removeCartItemStatus",
  async (
    { api, cart_item_id }: { api: NapiClient; cart_item_id: Ancillary["id"] },
    { getState, dispatch }
  ) => {
    const state = getState() as CheckoutReduxStateV2 | ResultsReduxState;
    const cart_id =
      (state as CheckoutReduxStateV2).cart?.id ||
      (state as ResultsReduxState).cart?.cart?.id;
    const cart_items = state.cart_items.value;

    if (!cart_id) {
      return;
    }

    // Optimistically remove the cart item from the cart
    dispatch(trackCartItemRemoval(cart_item_id));
    try {
      const updated_cart = await api.unselectAncillaryForCart(
        cart_id,
        cart_item_id
      );
      dispatch(pollCartV2Success(updated_cart));
      const cart_item = cart_items?.find(({ id }) => id === cart_item_id);

      return {
        ...cart_item,
        status: "CANDIDATE"
      };
    } catch (err) {
      throw err;
    }
  }
);

interface FetchAncillaryItemsParams {
  cart_id: string;
  api: NapiClient;
  filter?: Ancillary["category"][];
}

interface ThunkConfig {
  dispatch: Dispatch;
}

export const fetchCartAvailableAncillaries = createAsyncThunk<
  Ancillary[],
  FetchAncillaryItemsParams,
  ThunkConfig
>("checkout/fetchAncillaryItems", async ({ cart_id, api, filter }) => {
  return api.getAvailableAncillariesForCart(cart_id, filter);
});
